import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowUpTrayIcon,
  CpuChipIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  Squares2X2Icon,
  SparklesIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { trackEvent } from '../../utils/analytics';

const HowItWorks: React.FC = () => {
  const { signIn, isAuthenticated } = useAuth();
  
  const steps = [
    {
      step: 1,
      icon: ArrowUpTrayIcon,
      title: 'Upload Your PDF',
      description: 'Click to select or drag and drop your PDF file. The file loads directly into your browser - no upload to any server required.',
      details: [
        'Files stay on your device',
        'No account or login needed',
        'Instant loading',
        'Works with any PDF',
        'Complete privacy guaranteed'
      ],
      color: 'from-primary to-secondary'
    },
    {
      step: 2,
      icon: EyeIcon,
      title: 'Preview & Edit Pages',
      description: 'View thumbnails of all pages in your PDF. Select or deselect specific pages you want to include, and even edit individual pages before processing.',
      details: [
        'Interactive page thumbnails',
        'Click to select/deselect pages',
        'Edit individual pages separately',
        'Remove unwanted slides easily',
        'Select all or none options'
      ],
      color: 'from-primary to-secondary'
    },
    {
      step: 3,
      icon: SparklesIcon,
      title: 'Apply Enhancements',
      description: 'Choose simple enhancements to make your notes easier to read and cheaper to print.',
      details: [
        'Invert Colors - Make notes easier to read',
        'Remove Backgrounds - Clean up scanned pages',
        'Black & White - Save printer ink',
        'Remove Logo - Delete watermarks from slides'
      ],
      color: 'from-primary to-secondary'
    },
    {
      step: 4,
      icon: Squares2X2Icon,
      title: 'Configure Layout',
      description: 'Arrange multiple slides per page. Choose from 1 to 8 rows and columns (up to 64 slides per page), and select orientation.',
      details: [
        'Flexible grid layout (1-8 rows/columns)',
        'Portrait or landscape orientation',
        'Up to 64 slides per page',
        '1 cm margins on both sides'
      ],
      color: 'from-primary to-secondary'
    },
    {
      step: 5,
      icon: CpuChipIcon,
      title: 'Process in Browser',
      description: 'Click "Enhance PDF" and watch your document transform. All processing happens locally in your browser.',
      details: [
        'Local processing',
        'Real-time progress tracking',
        'Typically completes in seconds',
        'Works offline once loaded',
        'No data sent anywhere'
      ],
      color: 'from-primary to-secondary'
    },
    {
      step: 6,
      icon: ArrowDownTrayIcon,
      title: 'Download Your PDF',
      description: 'Get your enhanced, print-optimized PDF instantly. Perfect clarity, white backgrounds, and professional formatting. No tracking, no history saved.',
      details: [
        'High-quality print-ready PDF',
        'Optimized DPI and margins',
        'Instant download',
        'No file retention',
        'Ready to print immediately'
      ],
      color: 'from-primary to-secondary'
    }
  ];

  const features = [
    {
      icon: CheckCircleIcon,
      title: 'Lightning Fast',
      description: 'Process PDFs in seconds, all in your browser with no upload delays'
    },
    {
      icon: CheckCircleIcon,
      title: 'Complete Privacy',
      description: 'Files never leave your device - guaranteed zero data collection'
    },
    {
      icon: CheckCircleIcon,
      title: 'Print-Optimized',
      description: 'Perfect DPI and margins designed for professional printing'
    },
    {
      icon: CheckCircleIcon,
      title: 'No Installation',
      description: 'Works in any modern browser - no apps or downloads needed'
    },
    {
      icon: CheckCircleIcon,
      title: 'Save Ink & Paper',
      description: 'Convert dark backgrounds to white, saving up to 90% on printing costs'
    },
    {
      icon: CheckCircleIcon,
      title: '100% Free',
      description: 'All features free forever - no trials, limits, or hidden fees'
    }
  ];

  return (
    <div className="min-h-screen text-text-main py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-block mb-4">
            <motion.a
              href="https://youtu.be/lnFAlNJJ2Z4?si=vCoOF5OIH91JCfFh"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-red-600/20 to-red-500/20 border border-red-500/40 text-red-400 hover:border-red-500/60 transition-all duration-200"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span>Watch Full Tutorial</span>
            </motion.a>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-text-main mb-6">
            How to Print
            <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Online Lecture Notes
            </span>
          </h1>
          <p className="text-xl text-text-muted max-w-3xl mx-auto">
            Transform your dark lecture slides into print-perfect PDFs in just 6 simple steps. 
            From upload to download in under 60 seconds.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative mb-20">
          {/* Connection Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-blue-200 to-purple-200 h-full hidden lg:block opacity-20"></div>
          
          <div className="space-y-12">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className={`relative flex items-center ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex-col lg:space-x-8 space-y-8 lg:space-y-0`}
                >
                  {/* Content */}
                  <div className={`flex-1 ${isEven ? 'lg:text-right' : 'lg:text-left'} text-center lg:text-left`}>
                    <div className="bg-white/80 dark:bg-glass/50 backdrop-blur-sm rounded-xl shadow-lg p-8 border border-black/10 dark:border-border-glow text-left">
                      <div className="flex items-center justify-center lg:justify-start mb-4">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r ${step.color} text-white font-bold text-sm mr-3`}>
                          {step.step}
                        </span>
                        <h3 className="text-2xl font-bold text-text-main">
                          {step.title}
                        </h3>
                      </div>
                      <p className="text-text-muted mb-6 leading-relaxed">
                        {step.description}
                      </p>
                      <ul className="space-y-2">
                        {step.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-center text-sm text-text-muted">
                            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${step.color} mr-2`}></div>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Icon - Centered on the connection line */}
                  {/* Icon - Centered on the connection line (hidden on small screens) */}
                  <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 lg:static lg:transform-none lg:translate-x-0">
                    <div className={`w-20 h-20 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center shadow-lg`}>
                      <IconComponent className="w-10 h-10 text-white" />
                    </div>
                  </div>

                  {/* Spacer for alternating layout */}
                  <div className="flex-1 hidden lg:block"></div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-center text-text-main mb-12">
            Why Choose Our Process?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-white/80 dark:bg-glass/30 backdrop-blur-sm rounded-lg p-6 text-center border border-black/10 dark:border-border-glow hover:border-primary/50 transition-colors shadow-sm">
                  <div className="bg-gradient-to-r from-primary to-secondary w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-text-main mb-2">{feature.title}</h3>
                  <p className="text-sm text-text-muted">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Demo Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white text-center"
        >
          <h2 className="text-3xl font-bold mb-4">
            Ready to Transform Your PDFs?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands using NotesCrafter. Android app free with ads, web requires Community Pass!
          </p>
          <div className="flex justify-center items-center">
            {isAuthenticated ? (
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                href="https://flow.notescrafter.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent('start_processing_clicked', {
                    source: 'how_it_works_cta',
                    device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
                  });
                }}
                className="group flex items-center space-x-3 bg-white hover:bg-gray-50 text-black font-semibold py-3 px-8 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/20 mx-auto"
              >
                <span>Start Processing</span>
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </motion.a>
            ) : (
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  trackEvent('sign_in_clicked', {
                    source: 'how_it_works_cta',
                    device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
                  });
                  signIn();
                }}
                className="group flex items-center space-x-3 bg-white hover:bg-gray-50 text-black font-medium py-3 px-6 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/20 mx-auto"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span>Sign in with Google</span>
              </motion.button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HowItWorks;